// AI 启发式：局势评估 / 卡牌价值 / 一 ply 前瞻模拟
import type { GameState, PlayerState, StatusInstance, Team } from "@/types/game";
import type { DeckCard } from "@/types/cards";
import { apply, type EngineAction } from "@/engine/GameEngine";
import { decideResponderAction, decidePickupAction } from "./responder";

// ---------------- 卡牌价值（手牌保留 / 弃牌选择共用） ----------------

const RESPONSE_VALUE: Record<string, number> = {
  无敌: 12,
  闪避: 8,
  虚灵: 8,
  反射: 7,
  解除: 6,
  拒援: 2,
};

// 单卡保留价值（越大越不该弃）
export function cardValue(card: DeckCard, holder?: PlayerState): number {
  if (card.name in RESPONSE_VALUE) return RESPONSE_VALUE[card.name];
  switch (card.kind) {
    case "补给牌": {
      if (card.name.includes("大生命")) return 7;
      if (card.name.includes("生命")) return 5;
      if (card.name.includes("魔法")) {
        // 没蓝条压力的低蓝英雄，魔法药水价值低
        return holder && holder.maxMp <= 40 ? 2 : 3;
      }
      return 4;
    }
    case "装备牌": {
      const b =
        (card.attackBonus ?? 0) * 1.2 +
        (card.defenseBonus ?? 0) * 1.2 +
        (card.magicDefenseBonus ?? 0) * 0.8 +
        (card.rangeBonus ?? 0) * 3;
      return 5 + b;
    }
    case "攻击牌":
      if (card.subkind === "TG") return 6;
      return 4;
    case "摄魂牌":
      return 5;
    case "法术牌": {
      // 英雄没有可用主动技能时，法术牌几乎无用
      const hasSkill = holder?.hero.skills.some(
        (s) => s.type === "active" && (s.manaCost ?? 0) <= holder.mp
      );
      return hasSkill ? 3 : 0.5;
    }
    case "公共法术牌":
      if (["沉默", "无间炼狱", "致命弱点", "嗜血", "贪婪"].includes(card.name)) {
        return 5;
      }
      return 4;
    case "效果牌":
      return ["缴械", "掠夺", "拾遗"].includes(card.name) ? 4 : 1;
    default:
      return 2;
  }
}

// ---------------- 状态价值（对持有者） ----------------

function statusValue(st: StatusInstance): number {
  switch (st.name) {
    case "嗜血":
      return 6;
    case "命运链接":
      return 0;
    case "寒冰":
      return -12;
    case "沉默":
      return -8;
    case "禁攻":
      return -8;
    case "无魔御":
      return -6;
    case "无间炼狱":
      return -8;
    case "致命弱点":
      return -10;
    case "剧毒":
      return -((st.modifiers.poison ?? 2) * 4);
    default:
      if (st.modifiers?.invulnerable) return 20;
      return 0;
  }
}

function equipValue(p: PlayerState): number {
  const w = p.equip.weapon;
  const a = p.equip.armor;
  const c = p.equip.accessory;
  let v = 0;
  for (const e of [w, a, c]) {
    if (!e) continue;
    v +=
      (e.attackBonus ?? 0) * 1.2 +
      (e.defenseBonus ?? 0) * 1.2 +
      (e.magicDefenseBonus ?? 0) * 0.8 +
      (e.rangeBonus ?? 0) * 3;
  }
  return v;
}

// 单个玩家的战力（不含阵营符号）
function playerValue(p: PlayerState): number {
  if (!p.isAlive) return 0;
  const handV = p.hand.reduce((sum, c) => sum + cardValue(c, p), 0);
  const statusV = p.statuses.reduce((s, st) => s + statusValue(st), 0);
  let v = p.hp + p.mp * 0.05 + handV + equipValue(p) + statusV;
  if (p.isDying) v -= 400; // 濒死重罚（一圈结束未被救即阵亡）
  return v;
}

// ---------------- 局势评估 ----------------

export const TERMINAL_SCORE = 1e6;

// team 视角的净优势分
export function evaluateState(state: GameState, team: Team): number {
  let score = 0;
  let alliesAlive = 0;
  let enemiesAlive = 0;
  for (const p of state.players) {
    const v = playerValue(p);
    if (p.team === team) {
      score += v;
      if (p.isAlive) alliesAlive++;
    } else {
      score -= v;
      if (p.isAlive) enemiesAlive++;
    }
  }
  if (state.phase === "game-over") {
    if (alliesAlive === 0) return -TERMINAL_SCORE;
    if (enemiesAlive === 0) return TERMINAL_SCORE;
  }
  return score;
}

// ---------------- 合法动作试算（安全网：引擎拒绝则返回 null） ----------------

export function tryApply(
  state: GameState,
  action: EngineAction
): GameState | null {
  try {
    return apply(state, action);
  } catch {
    return null;
  }
}

// ---------------- 一 ply 前瞻 ----------------

// 出牌后若开启响应窗口：让窗口内每个待决策玩家按 AI 响应策略行动，
// 返回窗口结算后的状态（含反射链）。用于出牌决策的收益预估。
export function simulateAfterAction(
  state: GameState,
  action: EngineAction
): GameState | null {
  const first = tryApply(state, action);
  if (!first) return null;
  let cur: GameState = first;
  let guard = 0;
  while (guard < 16) {
    if (cur.phase === "response" && cur.responseWindow) {
      const win = cur.responseWindow;
      const pid = win.responders.find((rid) => win.decisions[rid] === "pending");
      if (!pid) break;
      const decision = decideResponderAction(cur, pid);
      cur = tryApply(cur, decision) ?? apply(cur, { type: "pass-response", playerId: pid });
      guard++;
      continue;
    }
    if (cur.phase === "pickup-response" && cur.pickupWindow) {
      // 前瞻中拾遗窗口由 AI 自动决策（含人类座位：模拟其放弃，保证不卡住）
      const win = cur.pickupWindow;
      const pid = win.responders.find((rid) => win.decisions[rid] === "pending");
      if (!pid) break;
      const decision = decidePickupAction(cur, pid);
      cur = tryApply(cur, decision) ?? apply(cur, { type: "pass-pickup", playerId: pid });
      guard++;
      continue;
    }
    break;
  }
  return cur;
}

// 动作前瞻评分：执行方团队视角的局势分增量
export function scoreAction(
  state: GameState,
  action: EngineAction,
  team: Team
): number | null {
  const after = simulateAfterAction(state, action);
  if (!after) return null;
  return evaluateState(after, team) - evaluateState(state, team);
}
